import { Box, createStyles, makeStyles, useMediaQuery } from '@material-ui/core';
import { CheckCircleOutline } from '@material-ui/icons';
import cn from 'classnames';
import { useFormik } from 'formik';
import React, { useCallback, useEffect } from 'react';
import { useHistory } from 'react-router';

import DeepLinkSuccessful from '../../assets/images/DeepLinkSuccessful';
import { useBrandingContext } from '../../branding';
import Btn from '../../components/Btn/Btn';
import RenderText from '../../dictionaries/RenderText';
import { getFormikValidator } from '../../utils/getFormikValidator';
import useTimer from '../../utils/useTimer';
import AcceptTermsAndPolices from '../IdentificationConfirmation/AcceptTermsAndPolices';
import { requiredPoliciesAreAccepted } from '../IdentificationConfirmation/utils/requiredPoliciesAreAccepted';
import { useLegalPolicyAccepted } from '../IdentificationConfirmation/utils/useLegalAcceptance';
import { useAcceptLegalTermsMutation } from '../../generated/graphql';

const validator = getFormikValidator(
  {},
  undefined,
  undefined,
  {
    required: 'Debes seleccionar una opción',
  },
);

const REDIRECTION_TIMEOUT = 8;

function IdentityConfirmationDeepLinkSuccess() {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const history = useHistory();
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  const classes = useStyles();
  const { theme } = useBrandingContext();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [ acceptLegalTerms ] = useAcceptLegalTermsMutation();
  const policyUpToDate = useLegalPolicyAccepted();
  const {
    isActive: isTimerActive,
    remaining: remainingTime,
    reset: resetTimer,
  } = useTimer({ initialActive: false, stopAt: REDIRECTION_TIMEOUT });

  const handleContinue = useCallback(() => {
    history.replace('/');
  }, [ history ]);

  const formik = useFormik({
    initialValues: { policiesAndTermsA: false, policiesAndTermsB: false },
    validate: validator,
    validateOnMount: false,
    onSubmit: async ({
      policiesAndTermsA: termsAndConditions,
      policiesAndTermsB: dataProcessing,
    }) => {
      if (!policyUpToDate) {
        await acceptLegalTerms({
          variables: {
            termsAndConditions,
            dataProcessing,
          },
        });
      }
      handleContinue();
    },
  });

  const disableSubmit = !formik.isValid
    || !requiredPoliciesAreAccepted(formik.values, policyUpToDate);

  useEffect(() => {
    if (policyUpToDate) {
      resetTimer();
    }
  }, [ resetTimer, policyUpToDate ]);

  useEffect(() => {
    if (policyUpToDate && !isTimerActive && remainingTime === 0) {
      handleContinue();
    }
  }, [
    policyUpToDate,
    remainingTime,
    isTimerActive,
    handleContinue,
  ]);

  const titleRenderKey = isMobile ? 'validation_deep_link_success_title_mobile' : 'validation_deep_link_success_title_desktop';

  return (
    <Box className={classes.container}>
      <Box className={classes.wrapper}>
        {isMobile ? <CheckCircleOutline fontSize="large" color="primary" /> : null}
        <h1 className={cn(classes.title)}>
          <RenderText textKey={titleRenderKey} />
        </h1>
        <p className={classes.content}>
          <RenderText textKey="validation_deep_link_success_description" />
        </p>
        {!policyUpToDate ? (
          <Box>
            <AcceptTermsAndPolices formik={formik} />
          </Box>
        ) : null}
        <Box className={cn(classes.actions, { [classes.actionsRightAlign]: !policyUpToDate })}>
          <Btn
            variant="contained"
            color="primary"
            onClick={() => formik.handleSubmit()}
            disabled={disableSubmit}
            data-test="cy-confirmAddress-btn"
          >
            <RenderText textKey="validation_deep_link_next_button" />
          </Btn>
          {policyUpToDate ? (
            <Box className={classes.timer}>
              <RenderText textKey="validation_deep_link_success_redirection" context={{ remainingTime }} />
            </Box>
          ) : null}
        </Box>
      </Box>
      {!isMobile ? <DeepLinkSuccessful /> : null}
    </Box>
  );
}

const useStyles = makeStyles((theme) => createStyles({
  container: {
    margin: theme.spacing(0, 4),
    [theme.breakpoints.up('md')]: {
      display: 'flex',
      gap: theme.spacing(15),
      width: '80%',
      maxWidth: theme.spacing(120),
    },
  },
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    justifyContent: 'center',
    alignItems: 'center',
    [theme.breakpoints.up('md')]: {
      alignItems: 'unset',
    },
  },
  title: {
    margin: 0,
    fontSize: theme.spacing(4),
    whiteSpace: 'nowrap',
    color: theme.palette.indigo.main,
    [theme.breakpoints.up('md')]: {
      whiteSpace: 'pre',
    },
  },
  content: {
    margin: 0,
    fontSize: theme.spacing(1.75),
    color: theme.palette.text2.main,
    lineHeight: `${theme.spacing(2.55)}px`,
  },
  actions: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    width: '100%',
    gap: theme.spacing(2),
    [theme.breakpoints.up('md')]: {
      flexDirection: 'row',
    },
  },
  actionsRightAlign: {
    alignItems: 'flex-end',
    justifyContent: 'unset',
    [theme.breakpoints.up('md')]: {
      alignItems: 'unset',
      justifyContent: 'flex-end',
    },
  },
  timer: {
    fontSize: theme.spacing(1.75),
    color: theme.palette.lavender.main,
    fontWeight: 600,
  },
}));

export default IdentityConfirmationDeepLinkSuccess;
